import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type ProductKey = "app" | "grande_teste" | "ebook_12_personas" | "ebook_32_sentimentos";

export interface Entitlement {
  product_key: string;
  status: string;
  expires_at: string | null;
}

export interface Product {
  key: string;
  name: string;
  description: string | null;
  price_cents: number;
  billing: "subscription" | "one_time";
  billing_cycle: string | null;
  access_days: number | null;
  installment_count: number | null;
  installment_price_cents: number | null;
  active: boolean;
  display_order: number;
}

export const formatPrice = (cents: number) =>
  (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export function useProducts() {
  return useQuery<Product[]>({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("key, name, description, price_cents, billing, billing_cycle, access_days, installment_count, installment_price_cents, active, display_order")
        .order("display_order");
      if (error) throw error;
      return (data ?? []) as Product[];
    },
    staleTime: 60_000,
  });
}

export function useEntitlements() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  const query = useQuery<Entitlement[]>({
    queryKey: ["entitlements", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_entitlements")
        .select("product_key, status, expires_at")
        .eq("user_id", userId!);
      if (error) throw error;
      return (data ?? []) as Entitlement[];
    },
    staleTime: 15_000,
  });

  // Realtime: unlock the screen as soon as the payment webhook confirms.
  useEffect(() => {
    if (!userId) return;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    try {
      channel = supabase
        .channel(`entitlements-${userId}`)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "user_entitlements", filter: `user_id=eq.${userId}` },
          () => queryClient.invalidateQueries({ queryKey: ["entitlements", userId] }),
        )
        .subscribe();
    } catch (err) {
      // Realtime may be blocked (CSP, proxies, private mode). Never break the page for it.
      console.warn("Realtime de acessos indisponível:", err);
    }
    return () => {
      if (channel) {
        try {
          supabase.removeChannel(channel);
        } catch {
          /* noop */
        }
      }
    };
  }, [userId, queryClient]);


  const isActive = (key: ProductKey) => {
    const e = query.data?.find((x) => x.product_key === key);
    if (!e || e.status !== "active") return false;
    return !e.expires_at || new Date(e.expires_at) > new Date();
  };

  const isPending = (key: ProductKey) =>
    query.data?.some((x) => x.product_key === key && x.status === "pending") ?? false;

  return {
    entitlements: query.data ?? [],
    isLoading: query.isLoading,
    refetch: query.refetch,
    isActive,
    isPending,
  };
}
